import { useState, useRef, useCallback, useEffect } from "react";
import api, { formatApiError, buildDownloadUrl, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { usePaywall } from "@/context/PaywallContext";
import { useSearchParams, Link } from "react-router-dom";
import { PenTool } from "lucide-react";
import { RecentLogos } from "@/components/RecentLogos";
import { LogoNameEditor } from "@/components/LogoNameEditor";
import { RealFontsPanel } from "@/components/RealFontsPanel";
import Header from "@/components/Header";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { SwatchEditor } from "@/components/SwatchEditor";
import { Segmented } from "@/components/Segmented";
import { BrandGuideButton } from "@/components/BrandGuideButton";
import { SocialKitButton } from "@/components/SocialKitButton";
import {
  Upload, ImageIcon, Download, FileImage, FileText, Package, Loader2, X, Sparkles,
  Palette, Wand2, ArrowRight, Smartphone,
} from "lucide-react";

const ACCEPTED = ["image/png", "image/jpeg", "image/jpg", "image/webp"];

export default function Dashboard() {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [colorCount, setColorCount] = useState("8");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [variants, setVariants] = useState(null);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [redesignStyle, setRedesignStyle] = useState("modern");
  const [redesignBusy, setRedesignBusy] = useState(false);
  const [redesign, setRedesign] = useState(null);
  const [redesignVectorBusy, setRedesignVectorBusy] = useState(false);
  const [stickers, setStickers] = useState(null);
  const [stickersBusy, setStickersBusy] = useState(false);
  const [fillMode, setFillMode] = useState("flat");
  const [smoothness, setSmoothness] = useState("medium");
  const [swatches, setSwatches] = useState([]);
  const [swatchesLoading, setSwatchesLoading] = useState(false);
  const [paletteNonce, setPaletteNonce] = useState(0);
  const [logoName, setLogoName] = useState("");
  const inputRef = useRef(null);
  const paywall = usePaywall();
  const locked = !!result && !result.unlocked;
  const [params, setParams] = useSearchParams();
  const [recentKey, setRecentKey] = useState(0);
  const unlockResult = () => paywall.open({ job_id: result?.job_id, name: result?.name || result?.filename });

  const openJob = async (jobId) => {
    try {
      const { data } = await api.get(`/jobs/${jobId}`);
      setFile(null);
      setSwatches([]);
      setRedesign(null);
      setStickers(null);
      setResult(data);
      setVariants(null);
      loadVariants(data.job_id);
      setParams({ job: jobId }, { replace: true });
      setTimeout(() => document.querySelector('[data-testid="result-section"]')?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not open that logo");
    }
  };

  useEffect(() => {
    const id = params.get("job");
    if (id) openJob(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    const form = new FormData();
    form.append("file", file);
    form.append("color_count", colorCount);
    setSwatchesLoading(true);
    api
      .post("/palette", form, { headers: { "Content-Type": "multipart/form-data" } })
      .then(({ data }) => {
        if (!cancelled) setSwatches(data.swatches.map((s) => ({ source: s.hex, target: s.hex, coverage: s.coverage })));
      })
      .catch((err) => {
        if (!cancelled) toast.error(formatApiError(err.response?.data?.detail) || "Could not read colours");
      })
      .finally(() => {
        if (!cancelled) setSwatchesLoading(false);
      });
    return () => { cancelled = true; };
  }, [file, colorCount, paletteNonce]);

  const handleDownload = async (path, filename) => {
    try {
      await downloadFile(path, filename);
    } catch (err) {
      if (err.status === 402) unlockResult();
      else toast.error(err.message || "Download failed");
    }
  };

  const onPick = (f) => {
    if (!f) return;
    if (!ACCEPTED.includes(f.type)) {
      toast.error("Only PNG, JPG, or WEBP files are supported");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large — maximum 10 MB");
      return;
    }
    setFile(f);
    setLogoName(f.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").trim().slice(0, 60));
    setResult(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    onPick(f);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = () => {
    setFile(null);
    setSwatches([]);
    setResult(null);
    setVariants(null);
    setRedesign(null);
    setStickers(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const generateStickers = async () => {
    if (!result?.job_id) return;
    setStickersBusy(true);
    try {
      const { data } = await api.post(`/jobs/${result.job_id}/stickers/generate`);
      setStickers(data);
      toast.success("Sticker pack ready");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setStickersBusy(false);
    }
  };

  const loadVariants = async (jobId) => {
    setVariantsLoading(true);
    try {
      const { data } = await api.get(`/jobs/${jobId}/variants`);
      setVariants(data.variants);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setVariantsLoading(false);
    }
  };

  const aiRedesign = async () => {
    if (!result?.job_id) return;
    setRedesignBusy(true);
    setRedesign(null);
    try {
      const { data } = await api.post(`/jobs/${result.job_id}/ai-redesign`, {
        style: redesignStyle,
      });
      setRedesign({
        style: data.style,
        image_url: buildDownloadUrl(data.image_url),
        vectorize_path: data.vectorize_url,
      });
      toast.success("AI redesign ready");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRedesignBusy(false);
    }
  };

  const vectorizeRedesign = async () => {
    if (!result?.job_id || !redesign) return;
    setRedesignVectorBusy(true);
    try {
      const { data } = await api.post(
        `/jobs/${result.job_id}/redesign/${redesign.style}/vectorize?color_count=${colorCount}`
      );
      setResult(data);
      setVariants(null);
      setRedesign(null);
      setRecentKey((k) => k + 1);
      setParams({ job: data.job_id }, { replace: true });
      // Auto-load variants for the new vector
      loadVariants(data.job_id);
      toast.success("Redesign vectorized");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setRedesignVectorBusy(false);
    }
  };

  const submit = async () => {
    if (!file) {
      toast.error("Please choose a logo to vectorize");
      return;
    }
    const form = new FormData();
    form.append("file", file);
    form.append("color_count", colorCount);
    form.append("fill_mode", fillMode);
    if (logoName.trim()) form.append("name", logoName.trim());
    form.append("smoothness", smoothness);
    if (swatches.length) {
      form.append("palette", JSON.stringify(swatches.map(({ source, target }) => ({ source, target }))));
    }
    setBusy(true);
    try {
      const { data } = await api.post("/vectorize", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setVariants(null);
      setRedesign(null);
      setStickers(null);
      setRecentKey((k) => k + 1);
      setParams({ job: data.job_id }, { replace: true });
      loadVariants(data.job_id);
      if (data.auto_bumped) {
        toast.success(
          `Bumped to ${data.color_count} colors (source was ${data.source_size}px) — preserves small accents.`
        );
      } else if (data.fill_mode === "gradient") {
        toast.success(`Vectorization complete · ${data.gradients_used} gradient${data.gradients_used === 1 ? "" : "s"} detected`);
      } else {
        toast.success("Vectorization complete");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-page">
      <Header />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-2">Workspace</div>
            <h1 className="text-3xl md:text-4xl tracking-tighter font-black">Vectorize a logo</h1>
          </div>
          <div className="text-xs text-[#52525B] uppercase tracking-[0.2em]">
            Drop. Configure. Download.
          </div>
        </div>

        <RecentLogos activeId={result?.job_id} onOpen={openJob} refreshKey={recentKey} />

        <Link to="/sketch" data-testid="dashboard-sketch-link"
          className="group mb-8 flex items-center justify-between gap-4 border border-black/10 hover:border-[#ED5A24] px-5 py-4 transition-colors duration-200">
          <span className="flex items-center gap-3 text-sm"><PenTool className="w-4 h-4 text-[#ED5A24]" />
            <span><strong>Only have a sketch?</strong> <span className="text-[#52525B]">Turn your drawing into a polished logo with AI.</span></span></span>
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* DROP ZONE */}
          <div className="md:col-span-8">
            <div
              data-testid="drop-zone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              className={`border border-dashed cursor-pointer p-8 min-h-[360px] flex items-center justify-center transition-colors duration-200 ${
                dragOver ? "border-[#ED5A24] bg-[#ED5A24]/[0.04]" : "border-black/25 bg-[#F8F9FA] hover:border-[#ED5A24]"
              }`}
            >
              <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={(e) => onPick(e.target.files?.[0])}
                data-testid="file-input"
              />
              {!file ? (
                <div className="text-center max-w-md">
                  <Upload className="w-8 h-8 mx-auto text-[#ED5A24] mb-6" strokeWidth={1.5} />
                  <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-2">Drop logo here</div>
                  <div className="text-lg font-medium text-[#0A0A0B] mb-3">PNG, JPG, or WEBP</div>
                  <div className="text-sm text-[#52525B]">
                    Click to browse — even tiny screenshots work. We upscale & quantize before tracing.
                  </div>
                  <div className="text-xs text-[#52525B] mt-2" data-testid="upload-limits">Up to 10 MB and 6000 × 6000 px</div>
                </div>
              ) : (
                <div className="w-full flex flex-col items-center" data-testid="file-preview">
                  <div className="bg-white border border-black/10 p-6 mb-6">
                    <img
                      src={previewUrl}
                      alt="preview"
                      className="max-h-[260px] max-w-full object-contain"
                      style={{
                        backgroundImage:
                          "linear-gradient(45deg, #f3f4f6 25%, transparent 25%), linear-gradient(-45deg, #f3f4f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f3f4f6 75%), linear-gradient(-45deg, transparent 75%, #f3f4f6 75%)",
                        backgroundSize: "16px 16px",
                        backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
                      }}
                    />
                  </div>
                  <div className="text-xs uppercase tracking-[0.2em] text-[#52525B]" data-testid="file-name">{file.name}</div>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="clear-file-btn"
                    onClick={(e) => { e.stopPropagation(); reset(); }}
                    className="mt-4 rounded-none text-xs uppercase tracking-[0.2em] hover:bg-[#0A0A0B] hover:text-white"
                  >
                    <X className="w-3.5 h-3.5" /> Remove
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* OPTIONS PANEL */}
          <div className="md:col-span-4">
            <div className="border border-black/10 bg-[#F8F9FA] p-6 sticky top-24">
              <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-6">Configuration</div>

              <div className="space-y-6">
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B]">Color count</Label>
                  <Select value={colorCount} onValueChange={setColorCount}>
                    <SelectTrigger
                      data-testid="color-count-select"
                      className="mt-2 rounded-none bg-white border-black/15 focus:ring-2 focus:ring-[#ED5A24]"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2" data-testid="color-option-2">2 — Black & White</SelectItem>
                      <SelectItem value="4" data-testid="color-option-4">4 colors</SelectItem>
                      <SelectItem value="8" data-testid="color-option-8">8 colors</SelectItem>
                      <SelectItem value="16" data-testid="color-option-16">16 colors</SelectItem>
                      <SelectItem value="full" data-testid="color-option-full">Full color</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#52525B] mt-2 leading-relaxed">
                    Fewer colors = cleaner shapes for brand logos. Full color is best for illustrations.
                  </p>
                </div>

                <>
                    <div>
                      <Label htmlFor="logo-name" className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B]">Logo name</Label>
                      <input
                        id="logo-name"
                        maxLength={60}
                        value={logoName}
                        onChange={(e) => setLogoName(e.target.value)}
                        placeholder="e.g. Grand Barbers"
                        data-testid="logo-name-input"
                        className="mt-2 w-full h-11 px-3 bg-white border border-black/15 text-sm outline-none focus:ring-2 focus:ring-[#ED5A24]"
                      />
                      <p className="text-xs text-[#52525B] mt-2">Shown as the brand name on your brand guidelines.</p>
                    </div>
                    <Segmented
                      label="Edge smoothness"
                      testId="smoothness"
                      value={smoothness}
                      onChange={setSmoothness}
                      options={[
                        { value: "low", label: "Low" },
                        { value: "medium", label: "Medium" },
                        { value: "high", label: "High" },
                      ]}
                      hint={smoothness === "high" ? "Rounds off heavy pixel stair-steps. Tiny details may soften." : "Higher = smoother curves; lower = keeps fine detail."}
                    />
                    <Segmented
                      label="Fill style"
                      testId="fill-mode"
                      value={fillMode}
                      onChange={setFillMode}
                      options={[
                        { value: "flat", label: "Flat" },
                        { value: "gradient", label: "Gradient" },
                      ]}
                      hint={fillMode === "gradient" ? "Detects colour fades and rebuilds them as real vector gradients." : "Solid colours only — classic brand-mark output."}
                    />
                    {file && (
                      <SwatchEditor
                        swatches={swatches}
                        loading={swatchesLoading}
                        onChange={setSwatches}
                        onReset={() => setPaletteNonce((n) => n + 1)}
                      />
                    )}
                  </>

                <div className="pt-2 border-t border-black/10">
                  <Label className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B]">Outputs</Label>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {["SVG", "EPS", "AI", "PDF", "PNG", "JPG"].map((f) => (
                      <span key={f} className="text-[11px] tracking-[0.15em] font-bold px-2 py-1 bg-white border border-black/10 text-[#0A0A0B]">
                        {f}
                      </span>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={submit}
                  disabled={busy || !file}
                  data-testid="vectorize-btn"
                  className="w-full rounded-none bg-[#ED5A24] hover:bg-[#C44619] text-white text-xs uppercase tracking-[0.2em] font-bold h-12"
                >
                  {busy ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Vectorizing…</>
                  ) : (
                    <><Sparkles className="w-4 h-4" /> Vectorize logo</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* RESULT */}
        {result && (
          <div className="mt-12" data-testid="result-section">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-2 flex items-center gap-3">
                  <span>Output</span>
                </div>
                <h2 className="text-2xl md:text-3xl tracking-tight font-bold">
                  <LogoNameEditor
                    jobId={result.job_id}
                    name={result.name || result.filename}
                    onSaved={(name) => setResult((r) => ({ ...r, name }))}
                  />
                </h2>
              </div>
              <div className="flex flex-wrap gap-2">
                {locked && (
                  <button
                    type="button"
                    onClick={unlockResult}
                    data-testid="unlock-downloads-btn"
                    className="inline-flex items-center gap-2 bg-[#ED5A24] hover:bg-[#0A0A0B] text-white text-xs uppercase tracking-[0.2em] font-bold h-12 px-6 transition-colors duration-200"
                  >
                    <Lock className="w-4 h-4" /> Unlock this logo · {paywall.price}
                  </button>
                )}
                <BrandGuideButton jobId={result.job_id} filename={result.filename} />
                <SocialKitButton jobId={result.job_id} filename={result.filename} />
                <button
                  type="button"
                  onClick={() => handleDownload(result.downloads.zip, `${result.filename.replace(/\.[^.]+$/, "")}_vector_bundle.zip`)}
                  data-testid="download-zip-btn"
                  className="inline-flex items-center gap-2 rounded-none bg-[#0A0A0B] hover:bg-[#ED5A24] text-white text-xs uppercase tracking-[0.2em] font-bold h-12 px-6 transition-colors duration-200 cursor-pointer"
                >
                  <Package className="w-4 h-4" /> Download bundle (.zip)
                </button>
              </div>
            </div>

            {/* BEFORE / AFTER STRIP */}
            <div
              data-testid="ba-strip"
              className="border border-black/10 bg-[#F8F9FA] p-4 mb-6 grid grid-cols-[1fr_auto_1fr] items-stretch gap-3"
            >
              <BAPanel
                testId="ba-strip-before"
                label="Source raster"
                tag={`${result.source_size || "?"}px`}
                pixelated
              >
                <img
                  src={buildDownloadUrl(`/api/jobs/${result.job_id}/original`)}
                  alt="Original raster"
                  className="max-w-full max-h-full object-contain"
                  style={{ imageRendering: "pixelated" }}
                />
              </BAPanel>

              <div className="flex items-center justify-center px-1">
                <ArrowRight className="w-6 h-6 text-[#ED5A24]" strokeWidth={2.5} />
              </div>

              <BAPanel
                testId="ba-strip-after"
                label="Vector trace"
                tag={result.palette ? `${result.palette.length} colours${result.gradients_used ? ` · ${result.gradients_used} gradients` : ""}` : `${result.color_count} colors`}
              >
                <img
                  src={buildDownloadUrl(`${result.downloads.png}?preview=1${result._v ? `&v=${result._v}` : ""}`)}
                  alt="Vector trace preview"
                  className="max-w-full max-h-full object-contain"
                />
              </BAPanel>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <div className="md:col-span-7 border border-black/10 bg-[#F8F9FA] p-6 md:p-8">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B] mb-4">Preview (SVG)</div>
                <div
                  data-testid="svg-preview"
                  className="w-full min-h-[300px] flex items-center justify-center bg-white border border-black/5 p-6"
                  style={{
                    backgroundImage:
                      "linear-gradient(45deg, #f3f4f6 25%, transparent 25%), linear-gradient(-45deg, #f3f4f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f3f4f6 75%), linear-gradient(-45deg, transparent 75%, #f3f4f6 75%)",
                    backgroundSize: "16px 16px",
                    backgroundPosition: "0 0, 0 8px, 8px -8px, -8px 0",
                  }}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: result.svg }}
                />
              </div>

              <div className="md:col-span-5">
                <div className="border border-black/10">
                  {[
                    { key: "svg", label: "SVG", desc: "Vector for web", icon: FileImage },
                    { key: "pdf", label: "PDF", desc: "Print-ready", icon: FileText },
                    { key: "eps", label: "EPS", desc: "Adobe legacy", icon: FileText },
                    { key: "ai", label: "AI", desc: "Illustrator", icon: FileText },
                    { key: "png", label: "PNG", desc: "2048px HD", icon: ImageIcon },
                    { key: "jpg", label: "JPG", desc: "2048px HD", icon: ImageIcon },
                  ].map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => handleDownload(result.downloads[f.key], `${result.filename.replace(/\.[^.]+$/, "")}.${f.key}`)}
                      data-testid={`download-${f.key}-btn`}
                      className="w-full text-left flex items-center justify-between border-b border-black/10 last:border-b-0 px-5 py-4 bg-white hover:bg-[#F8F9FA] transition-colors duration-200 group cursor-pointer"
                    >
                      <div className="flex items-center gap-4">
                        <f.icon className="w-4 h-4 text-[#ED5A24]" strokeWidth={2} />
                        <div>
                          <div className="text-sm font-bold tracking-tight">{f.label}</div>
                          <div className="text-xs text-[#52525B]">{f.desc}</div>
                        </div>
                      </div>
                      <Download className="w-4 h-4 text-[#52525B] group-hover:text-[#ED5A24] transition-colors duration-200" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <RealFontsPanel
              result={result}
              onUpdated={(data) => {
                setResult({ ...data, _v: Date.now() });
                setStickers(null);
                setVariants(null);
                loadVariants(data.job_id);
              }}
            />

            {/* STYLE VARIANTS */}
            <div className="mt-12" data-testid="variants-section">
              <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-2 flex items-center gap-2">
                    <Palette className="w-3.5 h-3.5" /> Style variants
                  </div>
                  <h2 className="text-2xl md:text-3xl tracking-tight font-bold">Instant restyles</h2>
                  <p className="text-sm text-[#52525B] mt-2 max-w-xl">
                    Auto-generated from the vector — no AI required. Drop into briefs,
                    embroidery files or favicons.
                  </p>
                </div>
              </div>
              {variantsLoading || !variants ? (
                <div className="border border-black/10 bg-[#F8F9FA] p-12 text-center text-xs uppercase tracking-[0.3em] text-[#52525B]">
                  {variantsLoading ? "Rendering variants…" : "Variants will appear here"}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-px bg-black/10 border border-black/10 max-w-2xl">
                  {[
                    { key: "grayscale", label: "Grayscale", desc: "Luminance map" },
                    { key: "outline", label: "Outline", desc: "Stroke only" },
                  ].map((v) => (
                    <div
                      key={v.key}
                      data-testid={`variant-${v.key}`}
                      className="bg-white p-5 flex flex-col"
                    >
                      <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B] mb-3">
                        {v.label}
                      </div>
                      <div
                        className="w-full aspect-square flex items-center justify-center border border-black/5 p-3 mb-3"
                        style={{
                          backgroundImage: "linear-gradient(45deg, #f3f4f6 25%, transparent 25%), linear-gradient(-45deg, #f3f4f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f3f4f6 75%), linear-gradient(-45deg, transparent 75%, #f3f4f6 75%)",
                          backgroundSize: "12px 12px",
                          backgroundPosition: "0 0, 0 6px, 6px -6px, -6px 0",
                          backgroundColor: "#fff",
                        }}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: variants[v.key] }}
                      />
                      <div className="text-xs text-[#52525B] mb-3">{v.desc}</div>
                      <div className="flex gap-1 mt-auto">
                        {["svg", "png", "pdf"].map((fmt) => (
                          <button
                            key={fmt}
                            type="button"
                            onClick={() => handleDownload(`/api/jobs/${result.job_id}/variants/${v.key}/file/${fmt}`, `${result.filename.replace(/\.[^.]+$/, "")}_${v.key}.${fmt}`)}
                            data-testid={`variant-${v.key}-${fmt}`}
                            className="flex-1 text-center text-[10px] tracking-[0.15em] uppercase font-bold py-2 border border-black/15 hover:bg-[#0A0A0B] hover:text-white hover:border-[#0A0A0B] transition-colors duration-200 cursor-pointer"
                          >
                            {fmt}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* AI REDESIGN */}
            <div className="mt-12 bg-[#0A0A0B] text-white" data-testid="redesign-section">
              <div className="p-8 md:p-12">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
                  <div className="md:col-span-5">
                    <div className="text-xs uppercase tracking-[0.3em] font-bold text-white/50 mb-3 flex items-center gap-2">
                      <Wand2 className="w-3.5 h-3.5" /> AI Redesign
                    </div>
                    <h2 className="text-3xl md:text-4xl tracking-tighter font-black leading-tight mb-4">
                      Reimagine the<br />
                      <span className="bg-gradient-to-r from-[#FF8A5C] to-[#6BB6FF] bg-clip-text text-transparent">
                        same idea.
                      </span>
                    </h2>
                    <p className="text-sm text-white/70 leading-relaxed mb-6">
                      Gemini Nano Banana redesigns your logo while keeping the
                      core concept. Pick a direction, generate, then vectorize
                      the result in one click.
                    </p>

                    <div className="space-y-3">
                      <Label className="text-[11px] uppercase tracking-[0.2em] font-bold text-white/50">Direction</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { key: "modern", label: "Modern" },
                          { key: "minimal", label: "Minimal" },
                          { key: "playful", label: "Playful" },
                          { key: "corporate", label: "Corporate" },
                          { key: "retro", label: "Retro" },
                        ].map((s) => (
                          <button
                            key={s.key}
                            type="button"
                            data-testid={`redesign-style-${s.key}`}
                            onClick={() => setRedesignStyle(s.key)}
                            className={`text-left text-xs uppercase tracking-[0.18em] font-bold px-3 py-3 border transition-colors duration-200 ${
                              redesignStyle === s.key
                                ? "bg-white text-[#0A0A0B] border-white"
                                : "bg-transparent border-white/20 text-white hover:border-white/50"
                            }`}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>

                      <Button
                        onClick={aiRedesign}
                        disabled={redesignBusy}
                        data-testid="ai-redesign-btn"
                        className="w-full mt-4 rounded-none bg-gradient-to-r from-[#ED5A24] to-[#1F8FED] hover:from-[#C44619] hover:to-[#0F6FBF] text-white text-xs uppercase tracking-[0.2em] font-bold h-12"
                      >
                        {redesignBusy ? (
                          <><Loader2 className="w-4 h-4 animate-spin" /> Redesigning…</>
                        ) : (
                          <><Wand2 className="w-4 h-4" /> Generate redesign</>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="md:col-span-7">
                    <div className="border border-white/10 bg-white/[0.03] p-4 md:p-6 min-h-[320px] flex items-center justify-center">
                      {redesignBusy ? (
                        <div className="text-center" data-testid="redesign-loading">
                          <Loader2 className="w-8 h-8 animate-spin text-white/60 mx-auto mb-4" />
                          <div className="text-xs uppercase tracking-[0.3em] text-white/60">
                            Brewing your new mark…
                          </div>
                        </div>
                      ) : redesign ? (
                        <div className="w-full" data-testid="redesign-result">
                          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-white/60">
                              Result · {redesign.style}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleDownload(`/api/jobs/${result.job_id}/redesign/${redesign.style}/image`, `${result.filename.replace(/\.[^.]+$/, "")}_redesign_${redesign.style}.png`)}
                                data-testid="download-redesign-png"
                                className="inline-flex items-center gap-2 rounded-none border border-white/30 hover:bg-white hover:text-[#0A0A0B] text-[11px] uppercase tracking-[0.2em] font-bold h-9 px-3 transition-colors duration-200 cursor-pointer"
                              >
                                <Download className="w-3.5 h-3.5" /> PNG
                              </button>
                              <Button
                                onClick={vectorizeRedesign}
                                disabled={redesignVectorBusy}
                                data-testid="vectorize-redesign-btn"
                                size="sm"
                                className="rounded-none bg-white text-[#0A0A0B] hover:bg-gradient-to-r hover:from-[#FF8A5C] hover:to-[#6BB6FF] hover:text-white text-[11px] uppercase tracking-[0.2em] font-bold h-9 px-4"
                              >
                                {redesignVectorBusy ? (
                                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Vectorizing…</>
                                ) : (
                                  <>Vectorize this <ArrowRight className="w-3.5 h-3.5" /></>
                                )}
                              </Button>
                            </div>
                          </div>
                          <div className="bg-white p-4">
                            <BeforeAfterSlider
                              beforeSrc={buildDownloadUrl(`/api/jobs/${result.job_id}/original`)}
                              afterSrc={redesign.image_url}
                              beforeLabel="Original"
                              afterLabel={`AI · ${redesign.style}`}
                            />
                            <div className="mt-3 text-[11px] text-[#52525B] text-center tracking-[0.15em] uppercase">
                              Drag the handle to compare
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center px-6" data-testid="redesign-empty">
                          <Sparkles className="w-6 h-6 text-white/40 mx-auto mb-4" strokeWidth={1.5} />
                          <div className="text-xs uppercase tracking-[0.3em] text-white/40">
                            Pick a direction → generate
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* iPHONE STICKERS */}
            <div className="mt-12" data-testid="stickers-section">
              <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#52525B] mb-2 flex items-center gap-2">
                    <Smartphone className="w-3.5 h-3.5" /> iPhone stickers
                  </div>
                  <h2 className="text-2xl md:text-3xl tracking-tight font-bold">Live-sticker pack</h2>
                  <p className="text-sm text-[#52525B] mt-2 max-w-xl">
                    Three Apple-style sticker sizes (300 / 408 / 618 px) with the
                    signature white outline + soft shadow. Drop into iMessage,
                    WhatsApp or Telegram.
                  </p>
                </div>
                {!stickers && (
                  <Button
                    onClick={generateStickers}
                    disabled={stickersBusy}
                    data-testid="generate-stickers-btn"
                    className="rounded-none bg-gradient-to-r from-[#ED5A24] to-[#1F8FED] hover:from-[#C44619] hover:to-[#0F6FBF] text-white text-xs uppercase tracking-[0.2em] font-bold h-12 px-6"
                  >
                    {stickersBusy ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Building…</>
                    ) : (
                      <><Smartphone className="w-4 h-4" /> Generate stickers</>
                    )}
                  </Button>
                )}
                {stickers && (
                  <button
                    type="button"
                    onClick={() => handleDownload(stickers.zip, `${result.filename.replace(/\.[^.]+$/, "")}_stickerpack.zip`)}
                    data-testid="download-stickers-zip-btn"
                    className="inline-flex items-center gap-2 rounded-none bg-[#0A0A0B] hover:bg-[#ED5A24] text-white text-xs uppercase tracking-[0.2em] font-bold h-12 px-6 transition-colors duration-200 cursor-pointer"
                  >
                    <Package className="w-4 h-4" /> Download sticker pack
                  </button>
                )}
              </div>

              {!stickers ? (
                <div
                  className="border border-dashed border-black/15 bg-[#F8F9FA] p-12 text-center"
                  data-testid="stickers-empty"
                >
                  <Smartphone className="w-8 h-8 mx-auto text-[#ED5A24] mb-4" strokeWidth={1.5} />
                  <div className="text-sm font-medium mb-2">No sticker pack yet</div>
                  <p className="text-xs text-[#52525B]">
                    Click "Generate stickers" to render Apple-style sticker
                    sizes from this vector.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/10 border border-black/10">
                  {[
                    { key: "small", label: "Small", px: 300 },
                    { key: "medium", label: "Medium", px: 408 },
                    { key: "large", label: "Large", px: 618 },
                  ].map((s) => (
                    <div
                      key={s.key}
                      data-testid={`sticker-${s.key}`}
                      className="bg-white p-5 flex flex-col"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B]">
                          {s.label}
                        </div>
                        <span className="text-[10px] tracking-[0.15em] uppercase font-bold px-2 py-0.5 bg-[#0A0A0B] text-white">
                          {s.px}px
                        </span>
                      </div>
                      <div
                        className="w-full aspect-square flex items-center justify-center border border-black/5 p-3 mb-3"
                        style={{
                          backgroundImage:
                            "linear-gradient(45deg, #f3f4f6 25%, transparent 25%), linear-gradient(-45deg, #f3f4f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f3f4f6 75%), linear-gradient(-45deg, transparent 75%, #f3f4f6 75%)",
                          backgroundSize: "12px 12px",
                          backgroundPosition: "0 0, 0 6px, 6px -6px, -6px 0",
                          backgroundColor: "#fff",
                        }}
                      >
                        <img
                          src={buildDownloadUrl(`${stickers.previews[s.key]}?preview=1`)}
                          alt={`${s.label} sticker`}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleDownload(stickers.downloads[s.key], `${result.filename.replace(/\.[^.]+$/, "")}_sticker_${s.key}_${s.px}.png`)}
                        data-testid={`download-sticker-${s.key}-btn`}
                        className="mt-auto text-center text-[11px] tracking-[0.15em] uppercase font-bold py-2 border border-black/15 hover:bg-[#0A0A0B] hover:text-white hover:border-[#0A0A0B] transition-colors duration-200 cursor-pointer"
                      >
                        <Download className="w-3.5 h-3.5 inline mr-1" /> PNG
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BAPanel({ testId, label, tag, children }) {
  return (
    <div
      data-testid={testId}
      className="relative bg-white border border-black/5"
      style={{
        backgroundImage:
          "linear-gradient(45deg, #f3f4f6 25%, transparent 25%), linear-gradient(-45deg, #f3f4f6 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f3f4f6 75%), linear-gradient(-45deg, transparent 75%, #f3f4f6 75%)",
        backgroundSize: "10px 10px",
        backgroundPosition: "0 0, 0 5px, 5px -5px, -5px 0",
      }}
    >
      <div className="absolute top-2 left-2 z-10 text-[9px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 bg-[#0A0A0B] text-white">
        {label}
      </div>
      <div className="absolute top-2 right-2 z-10 text-[9px] uppercase tracking-[0.15em] font-bold px-2 py-0.5 bg-white border border-black/10 text-[#52525B]">
        {tag}
      </div>
      <div className="w-full h-[160px] md:h-[200px] flex items-center justify-center p-4">
        {children}
      </div>
    </div>
  );
}
